import React, {useEffect, useState} from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import {
  Col,
  FormGroup,
  Form,
  FormText,
  CardBody,
  Row,
  Button,
  Card,
  CardHeader, Input
} from "reactstrap";
import { api } from '../../utils/index'
import SetTitle from '../../utils/set_title'

import { ToastContainer } from 'react-toastify'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import { useFileUpload } from '../../hooks'
import Header from "../../components/Headers/Header";

import { create, all } from 'mathjs'
const math = create(all)

const CategoryTextGenForm = () => {
  SetTitle('Generatore Testi per Categorie')
  const { id } = useParams()
  const history = useHistory()

  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [toneOfVoice, setToneOfVoice] = useState('')
  const [stopwords, setStopwords] = useState('')
  const [customPrompt, setCustomPrompt] = useState('');
  const [wordsCount, setWordsCount] = useState(500); // New state for words count
  const [file, setFile] = useState(null)
  const [jobStatus, setJobStatus] = useState(null)
  const [progress, setProgress] = useState('0/0');
  const [isEditMode, setIsEditMode] = useState(false); // New state to track edit/view mode
  const { upload, loading } = useFileUpload()

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isEditMode) { // Prevent form submission if in edit mode
      notify('Non è possibile creare un nuovo job in modalità visualizzazione. Utilizza il pulsante "Riprova falliti" se disponibile.', 'warning');
      return;
    }
    if (!name || !brand || !file) { // File is only required in create mode
      notify('Per favore, compila il nome del job, il brand e carica un file.', 'error')
      return
    }

    let formData = new FormData();
    formData.append('name', name)
    formData.append('brand', brand)
    formData.append('tone_of_voice', toneOfVoice)
    formData.append('stopwords', stopwords)
    formData.append('custom_prompt', customPrompt)
    formData.append('words_count', wordsCount) // Add wordsCount to form data
    formData.append('file', file)

    try {
      await upload('post', `category-text-gen/`, formData, {
        onSuccess: (data) => {
          notify("Job creato con successo. L'elaborazione inizierà a breve.", 'success')
          setTimeout(() => history.push('/category-text-gen'), 500)
        },
        onError: (errorMessage, err) => {
          notify(err.response?.data?.error || 'Si è verificato un errore.', 'warning')
        },
      })
    } catch (err) {
      console.error('Category Text Gen submission failed:', err)
      notify('Si è verificato un errore inaspettato durante l\'invio.', 'error')
    }
  }

  const handleUpdate = async () => {
    if (!id) return;
    try {
      await api.put(`category-text-gen/${id}/`, {
        custom_prompt: customPrompt
      });
      notify("Prompt aggiornato con successo.", 'success');
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      console.error('Category Text Gen update failed:', err);
      const errorMessage = err.response?.data?.error || "Si è verificato un errore durante l'aggiornamento.";
      notify(errorMessage, 'error');
    }
  };

  const handleRetry = async () => {
    if (!id) return;
    try {
      await api.post(`category-text-gen/${id}/retry_failed/`);
      notify("Il processo di recupero per i job falliti è stato avviato.", 'success');
      // Optionally, redirect or update the status on the page
      setTimeout(() => history.push('/category-text-gen'), 500);
    } catch (err) {
      notify(err.response?.data?.error || "Si è verificato un errore durante il tentativo di recupero.", 'error');
    }
  };

  useEffect(() => {
    setIsEditMode(!!id); // Set edit mode if ID is present

    if (id) {
      api.get(`category-text-gen/${id}/`)
        .then(res => {
          const job = res.data;
          setName(job.name);
          setBrand(job.brand);
          setToneOfVoice(job.tone_of_voice || '');
          setStopwords(job.stopwords || '');
          setCustomPrompt(job.custom_prompt || '');
          setWordsCount(job.words_count || 500); // Set wordsCount from job details, default to 500
          setJobStatus(job.status);
          setProgress(job.progress || '0/0');
        })
        .catch(err => {
          console.error("Failed to fetch job details", err);
          notify("Impossibile caricare i dettagli del job.", 'error');
          history.push('/category-text-gen'); // Redirect if job not found or error
        });
    }
  }, [id, history]); // Depend on id and history for potential redirect

  return (
    <Header
      iconClass="fa-solid fa-file-lines text-white"
      title="Generatore Testi per Categorie"
      description={"Genera descrizioni ottimizzate per la SEO per le pagine di categoria, analizzando i prodotti presenti sulla pagina."}
    >
      <div className="pb-5">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col md={10}>
                <h1 className="mb-0">{id ? 'Visualizza' : 'Nuovo'} Job di Generazione Testi</h1>
              </Col>
              <Col md={2} style={{ textAlign: "right" }}>
                <Button color="primary" onClick={() => history.push('/category-text-gen')}>&larr; Torna alla Lista</Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <Form onSubmit={handleSubmit} id="category-text-gen-form">
                <h6 className="heading-small text-muted mb-4">Configurazione del Job</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="name" className="form-control-label">
                        Nome del Job
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. Collezione Estiva [brand]"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={isEditMode} // Disable in edit mode
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    {/* FileInput should be disabled or hidden in edit mode */}
                    {!isEditMode && <FileInput
                      onFileChange={setFile}
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      helpText={<>Carica un file Excel con le colonne 'name' e 'link' per ogni categoria.</>}
                      required={!id}
                    />}
                    {isEditMode && (
                        <FormGroup>
                            <label className="form-control-label">File caricato</label>
                            <p className="form-control-static">
                                {jobStatus === 'CREATED' || jobStatus === 'RETRIEVING_DATA' ? 'File in elaborazione' : 'File caricato in precedenza'}
                            </p>
                            <FormText color="muted">
                                Per modificare le categorie, è necessario creare un nuovo job.
                            </FormText>
                        </FormGroup>
                    )}
                  </Col>
                </Row>
                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">Parametri di Generazione Testo</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="brand" className="form-control-label">
                        Nome del Brand
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="Inserisci il nome del brand"
                        id="brand"
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        required
                        disabled={isEditMode} // Disable in edit mode
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="tone_of_voice" className="form-control-label">
                        Tone of Voice (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. Giocoso ed energico"
                        id="tone_of_voice"
                        value={toneOfVoice}
                        onChange={(e) => setToneOfVoice(e.target.value)}
                        disabled={isEditMode} // Disable in edit mode
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="words_count" className="form-control-label">
                        Numero di Parole (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="number"
                        placeholder="es. 500"
                        id="words_count"
                        value={wordsCount}
                        onChange={(e) => setWordsCount(e.target.value)}
                        disabled={isEditMode} // Disable in edit mode
                      />
                      <FormText color="muted">
                        Numero approssimativo di parole per il testo generato. Default: 500.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="stopwords" className="form-control-label">
                        Stopwords (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. economico, gratis, limitato"
                        id="stopwords"
                        value={stopwords}
                        onChange={(e) => setStopwords(e.target.value)}
                        disabled={isEditMode} // Disable in edit mode
                      />
                      <FormText color="muted">
                        Lista di parole separate da virgola da evitare nel testo generato.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <FormGroup>
                      <label htmlFor="custom_prompt" className="form-control-label">
                        Custom Prompt (Opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="textarea"
                        rows="5"
                        placeholder="Se fornito, questo prompt sostituirà quello di default per la generazione del testo."
                        id="custom_prompt"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        disabled={false}
                      />
                      <FormText color="muted">
                        Devi inserire queste variabili nel prompt: {"{brand}"}, {"{category_name}"}, {"{data}"}, {"{stopwords}"}, {"{tone_of_voice}"}, {"{words_count}"}.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mt-4 mb-3">
                  <Col className="text-center">
                    {id && jobStatus === 'ERROR' && (
                      <Button color="warning" size="lg" onClick={handleRetry} className="mr-3" disabled={loading}>
                        Riprova falliti
                      </Button>
                    )}
                    {isEditMode && (
                        <Button color="primary" size="lg" onClick={handleUpdate} disabled={loading} className="mr-3">Aggiorna Prompt</Button>
                    )}
                    {isEditMode && math.evaluate(progress || '0/1') === 1 && (
                        <a href={`${api.defaults.baseURL}category-text-gen/${id}/download_excel/`} download>
                            <Button color="success" size="lg" type="button" disabled={loading} className="mr-3"><i className="fas fa-file-download" /> Scarica Excel</Button>
                        </a>
                    )}
                    {!isEditMode && <Button color="info" size="lg" type="submit" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : 'Crea Job'}
                    </Button>}
                  </Col>
                </Row>
              </Form>
            )}
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </Header>
  )
}
export default CategoryTextGenForm